import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import * as trackerTypes from 'app/interfaces/actiontracker.interface';
import { DataLayerService } from 'app/services/datalayer.service';
import { DateTime } from 'luxon';

import { HotjarService } from './hotjar.service';
import { UsetifulService } from './usetiful.service';
import { ZkuService } from './zku.service';

@Injectable()
export class ActionTrackerService {
	constructor(
		private hotjarService: HotjarService,
		private usetifulService: UsetifulService,
		private zkuService: ZkuService,
		public dataLayerService: DataLayerService
	) {
		const userData = this.zkuService.getZku();

		if (userData) {
			const birthDate = DateTime.fromISO(userData.birthdate);
			const userAge = DateTime.now()
				.diff(birthDate, 'years')
				.years.toPrecision(2);

			const userIdentification = {
				acceptsHalfHour: userData.acceptsHalfHour,
				age: userAge === 'NaN' ? undefined : userAge,
				blockNewClients: userData.blockNewClients,
				city: userData.legalPerson?.city,
				especialty: userData.mainType,
				gender: userData.gender,
				isCompleteOnboard: userData.isCompleteOnboard,
				isOnline: userData.isOnline,
				isPremium: userData.isPremium,
				trainee: userData.trainee,
			};

			this.dataLayerService.pushToDataLayer({
				...userIdentification,
				userId: userData.id,
				userEmail: userData.email,
				professionalAvatar: userData.imageUrl,
				professionalType: userData.mainType,
				userType: 'professional',
				name: userData.name,
			});

			this.usetifulService.identifyUser({
				...userIdentification,
				userId: userData.id,
			});

			this.hotjarService.identifyUser(userData.id, userIdentification);
		}
	}

	eventTracker(
		scope: trackerTypes.SCOPE_TYPE,
		subscope: trackerTypes.SUBSCOPE_TYPE,
		item: trackerTypes.ITEM_TYPE,
		interaction: trackerTypes.INTERACTION_TYPE,
		payload?: { [key: string]: any }
	) {
		const payloadData = payload ?? {};

		payloadData.interaction = interaction;
		const eventName = 'at_' + scope + '_' + subscope + '_' + item;

		if (window.location.hostname.includes('localhost')) {
			console.log(eventName, payload);
		}

		if (eventName.length > 40) {
			console.warn(
				'Event name length is bigger than expected. Analytics will cut off names that exceed 40 characters. Please rename your event.',
				eventName
			);
		}

		Sentry.addBreadcrumb({
			category: 'ui.tracker',
			message: eventName,
			data: payloadData,
			level: 'info',
			type: 'default',
		});

		this.dataLayerService.pushToDataLayer({
			event: eventName,
			...payloadData,
		});
	}
}
